/**
 * StatMooseGameCast - ESPN-Gamecast-style live scoreboard, box score, and PBP.
 * Supports all 6 sports via a single unified component.
 * Primary transport: SSE with polling fallback.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, Clock, ChevronDown, ChevronUp, Filter,
  Users, Trophy, Zap, Circle, AlertTriangle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ═══════════════════════════════════════════
   DATA HOOK — SSE primary, polling fallback
   ═══════════════════════════════════════════ */
const useGameCastData = (code) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [transport, setTransport] = useState("connecting"); // "sse" | "polling" | "connecting"
  const mountedRef = useRef(true);
  const esRef = useRef(null);
  const pollRef = useRef(null);

  const handlePayload = useCallback((payload) => {
    if (!mountedRef.current) return;
    setData(payload);
    setError(null);
  }, []);

  // Polling fallback
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    setTransport("polling");
    const poll = async () => {
      if (!mountedRef.current) return;
      try {
        const r = await fetch(`${API}/gamecast/${code}`);
        if (r.ok) handlePayload(await r.json());
      } catch (e) {
        if (mountedRef.current) setError("Connection lost");
      }
    };
    poll();
    pollRef.current = setInterval(poll, 8000);
  }, [code, handlePayload]);

  // SSE primary
  useEffect(() => {
    if (!code) return;
    mountedRef.current = true;
    let sseErrorCount = 0;

    // Initial fetch (so we have data before SSE connects)
    fetch(`${API}/gamecast/${code}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(handlePayload)
      .catch(() => {});

    try {
      const es = new EventSource(`${API}/gamecast/${code}/stream`);
      esRef.current = es;

      es.addEventListener("snapshot", (e) => {
        sseErrorCount = 0;
        setTransport("sse");
        handlePayload(JSON.parse(e.data));
      });
      es.addEventListener("update", (e) => {
        handlePayload(JSON.parse(e.data));
      });
      es.addEventListener("game_final", () => {
        es.close();
        // Do one final poll
        fetch(`${API}/gamecast/${code}`).then(r => r.ok ? r.json() : null).then(d => d && handlePayload(d));
      });
      es.addEventListener("max_lifetime", () => {
        es.close();
        // Reconnect
        setTimeout(() => {
          if (mountedRef.current) startPolling();
        }, 500);
      });
      es.onerror = () => {
        sseErrorCount++;
        if (sseErrorCount >= 2) {
          es.close();
          startPolling();
        }
      };
    } catch {
      startPolling();
    }

    return () => {
      mountedRef.current = false;
      esRef.current?.close();
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [code, handlePayload, startPolling]);

  return { data, error, transport };
};


/* ═══════════════════════════════════════════
   SCORING MAGNITUDE — consistent color coding
   ═══════════════════════════════════════════ */
const SCORE_TIERS = {
  // Football
  touchdown:  { tier: "big",    label: "TD",  color: "#22c55e" },
  td:         { tier: "big",    label: "TD",  color: "#22c55e" },
  "field goal":{ tier: "med",   label: "FG",  color: "#3b82f6" },
  fg:         { tier: "med",    label: "FG",  color: "#3b82f6" },
  safety:     { tier: "med",    label: "SAF", color: "#f97316" },
  "extra point":{ tier: "small",label: "XP",  color: "#a855f7" },
  pat:        { tier: "small",  label: "PAT", color: "#a855f7" },
  "two-point":{ tier: "med",    label: "2PT", color: "#f97316" },
  "2pt":      { tier: "med",    label: "2PT", color: "#f97316" },
  // Basketball
  "3pt":      { tier: "big",    label: "3PT", color: "#22c55e" },
  "three":    { tier: "big",    label: "3PT", color: "#22c55e" },
  dunk:       { tier: "big",    label: "DUNK",color: "#22c55e" },
  "2pt":      { tier: "med",    label: "2PT", color: "#3b82f6" },
  layup:      { tier: "med",    label: "LAY", color: "#3b82f6" },
  "free throw":{ tier: "small", label: "FT",  color: "#a855f7" },
  ft:         { tier: "small",  label: "FT",  color: "#a855f7" },
  // Baseball / Softball
  "home run": { tier: "big",    label: "HR",  color: "#22c55e" },
  hr:         { tier: "big",    label: "HR",  color: "#22c55e" },
  run:        { tier: "small",  label: "R",   color: "#3b82f6" },
  // Soccer
  goal:       { tier: "big",    label: "GOAL",color: "#22c55e" },
  // Volleyball
  point:      { tier: "small",  label: "PT",  color: "#3b82f6" },
  kill:       { tier: "med",    label: "KILL",color: "#3b82f6" },
  ace:        { tier: "med",    label: "ACE", color: "#22c55e" },
};

const getScoreTier = (action) => {
  if (!action) return null;
  const a = action.toLowerCase().trim();
  // Exact match first
  if (SCORE_TIERS[a]) return SCORE_TIERS[a];
  // Partial match
  for (const [key, val] of Object.entries(SCORE_TIERS)) {
    if (a.includes(key)) return val;
  }
  // Generic scoring fallback
  return { tier: "small", label: action, color: "#3b82f6" };
};

const TIER_STYLES = {
  big:   "animate-score-big ring-2 ring-green-500/60 scale-110",
  med:   "animate-score-med ring-1 ring-blue-400/50",
  small: "animate-score-sm",
};


/* ═══════════════════════════════════════════
   HEADER BAR
   ═══════════════════════════════════════════ */
const TeamLogo = ({ team, size = 48 }) => {
  if (team?.logo) {
    return <img src={team.logo} alt={team.name} style={{ width: size, height: size }} className="object-contain" />;
  }
  const bg = team?.color || "#333";
  return (
    <div
      className="rounded-full flex items-center justify-center font-black text-white"
      style={{ width: size, height: size, backgroundColor: bg, fontSize: size * 0.35 }}
    >
      {team?.abbr || team?.name?.[0] || "?"}
    </div>
  );
};

const StatusBadge = ({ status, statusDetail }) => {
  if (status === "active") {
    return (
      <div className="flex items-center gap-1.5 bg-red-600 px-2.5 py-1 rounded text-white text-xs font-bold uppercase tracking-wider">
        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
        {statusDetail || "LIVE"}
      </div>
    );
  }
  if (status === "final" || status === "completed") {
    return (
      <div className="bg-zinc-700 px-2.5 py-1 rounded text-white text-xs font-bold uppercase tracking-wider">
        {statusDetail || "FINAL"}
      </div>
    );
  }
  if (status === "halftime") {
    return (
      <div className="bg-yellow-600 px-2.5 py-1 rounded text-white text-xs font-bold uppercase tracking-wider">
        {statusDetail || "HALFTIME"}
      </div>
    );
  }
  return (
    <div className="bg-zinc-800 px-2.5 py-1 rounded text-zinc-300 text-xs font-bold uppercase tracking-wider">
      {statusDetail || "SCHEDULED"}
    </div>
  );
};

const GameCastHeader = ({ header, broadcastOverrides }) => {
  // Hooks must come before any early return
  const prevScoresRef = useRef({ home: null, away: null });
  const [homeAnim, setHomeAnim] = useState(null);
  const [awayAnim, setAwayAnim] = useState(null);

  useEffect(() => {
    if (!header) return;
    const prev = prevScoresRef.current;
    const currHome = header.home?.score ?? null;
    const currAway = header.away?.score ?? null;

    if (prev.home !== null && currHome !== null && currHome !== prev.home) {
      const diff = currHome - prev.home;
      const tier = diff >= 6 ? "big" : diff >= 2 ? "med" : "small";
      setHomeAnim(tier);
      setTimeout(() => setHomeAnim(null), 1800);
    }
    if (prev.away !== null && currAway !== null && currAway !== prev.away) {
      const diff = currAway - prev.away;
      const tier = diff >= 6 ? "big" : diff >= 2 ? "med" : "small";
      setAwayAnim(tier);
      setTimeout(() => setAwayAnim(null), 1800);
    }

    prevScoresRef.current = { home: currHome, away: currAway };
  }, [header]);

  if (!header) return null;
  const { home, away, status, status_detail, venue, period_label, period, clock } = header;

  // Merge admin overrides (logos, colors) as fallbacks
  const mergedHome = {
    ...home,
    logo: home?.logo || broadcastOverrides?.home?.logo_url || null,
    color: home?.color || broadcastOverrides?.home?.color || "#333",
    name: home?.name || broadcastOverrides?.home?.name || "Home",
    abbr: home?.abbr || broadcastOverrides?.home?.abbreviation || home?.name?.[0] || "H",
  };
  const mergedAway = {
    ...away,
    logo: away?.logo || broadcastOverrides?.away?.logo_url || null,
    color: away?.color || broadcastOverrides?.away?.color || "#333",
    name: away?.name || broadcastOverrides?.away?.name || "Away",
    abbr: away?.abbr || broadcastOverrides?.away?.abbreviation || away?.name?.[0] || "A",
  };

  const scoreClass = (anim, teamColor) => {
    const base = "text-xl sm:text-2xl md:text-3xl font-black tabular-nums transition-all duration-300";
    if (!anim) return `${base} text-white`;
    const glow = anim === "big"
      ? "score-flash-big"
      : anim === "med"
      ? "score-flash-med"
      : "score-flash-sm";
    return `${base} ${glow}`;
  };

  return (
    <div className="bg-zinc-900 border border-white/10" data-testid="gamecast-header">
      <div className="px-3 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Away team */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <TeamLogo team={mergedAway} size={36} />
            <div className="min-w-0 hidden sm:block">
              <p className="text-white font-bold text-sm sm:text-base truncate">{mergedAway.name}</p>
              <p className="text-zinc-500 text-xs">{mergedAway.abbr}{away?.record ? ` · ${away.record}` : ""}</p>
            </div>
            <div className="min-w-0 sm:hidden">
              <p className="text-white font-bold text-xs truncate">{mergedAway.abbr}</p>
            </div>
            <span className={`${scoreClass(awayAnim)} ml-auto`}>{away?.score ?? "–"}</span>
          </div>

          {/* Center status */}
          <div className="flex flex-col items-center gap-1 px-1.5 sm:px-3 flex-shrink-0">
            <StatusBadge status={status} statusDetail={status_detail} />
          </div>

          {/* Home team */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 flex-row-reverse">
            <TeamLogo team={mergedHome} size={36} />
            <div className="min-w-0 text-right hidden sm:block">
              <p className="text-white font-bold text-sm sm:text-base truncate">{mergedHome.name}</p>
              <p className="text-zinc-500 text-xs">{mergedHome.abbr}{home?.record ? ` · ${home.record}` : ""}</p>
            </div>
            <div className="min-w-0 text-right sm:hidden">
              <p className="text-white font-bold text-xs truncate">{mergedHome.abbr}</p>
            </div>
            <span className={`${scoreClass(homeAnim)} mr-auto`}>{home?.score ?? "–"}</span>
          </div>
        </div>

        {/* Venue only — no date */}
        {venue && (
          <p className="text-zinc-600 text-[10px] text-center mt-2 uppercase tracking-wider truncate">{venue}</p>
        )}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   SITUATION STRIP (sport-aware)
   ═══════════════════════════════════════════ */
const FootballSituation = ({ situation, header }) => {
  if (!situation) return null;
  return (
    <div className="overflow-x-auto bg-zinc-800/60 border-x border-b border-white/10">
      <div className="flex items-center justify-between gap-3 px-4 py-2 text-xs min-w-[320px]">
        {/* Away timeouts on left */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-zinc-600 text-[10px] uppercase">{header?.away?.abbr || "AWY"}</span>
          <div className="flex gap-0.5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-sm ${i < (situation.away_timeouts ?? 0) ? "bg-yellow-500" : "bg-zinc-700"}`} />
            ))}
          </div>
        </div>

        {/* Center — down & distance */}
        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          <span className="text-orange-400 font-bold whitespace-nowrap">{situation.down_and_distance || `${situation.down}${situation.down ? " & " : ""}${situation.distance}`}</span>
          {situation.ball_on && <span className="text-zinc-400 whitespace-nowrap hidden sm:inline">Ball on {situation.ball_on}</span>}
          {situation.drive_number && <span className="text-zinc-500 whitespace-nowrap hidden sm:inline">Drive #{situation.drive_number}</span>}
        </div>

        {/* Home timeouts on right */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="flex gap-0.5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-sm ${i < (situation.home_timeouts ?? 0) ? "bg-yellow-500" : "bg-zinc-700"}`} />
            ))}
          </div>
          <span className="text-zinc-600 text-[10px] uppercase">{header?.home?.abbr || "HME"}</span>
        </div>
      </div>
    </div>
  );
};

const BaseballSituation = ({ situation }) => {
  if (!situation) return null;
  const { bases, balls, strikes, outs, inning, inning_half, at_bat, on_mound } = situation;
  const isTop = inning_half === "top";

  return (
    <div className="overflow-x-auto bg-zinc-800/60 border-x border-b border-white/10">
      <div className="flex items-center justify-between gap-3 sm:gap-4 px-4 py-2.5 text-xs min-w-[300px]">
        {/* Inning */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex flex-col items-center leading-none">
            <span className={`text-[8px] ${isTop ? "text-white" : "text-zinc-600"}`}>▲</span>
            <span className="text-white font-bold text-sm">{inning}</span>
            <span className={`text-[8px] ${!isTop ? "text-white" : "text-zinc-600"}`}>▼</span>
          </div>
        </div>

        {/* Diamond */}
        <svg width="36" height="36" viewBox="0 0 36 36" className="flex-shrink-0">
          <rect x="12" y="1" width="12" height="12" rx="1" transform="rotate(45 18 7)" fill={bases?.second ? "#f97316" : "#3f3f46"} stroke="#52525b" strokeWidth="0.5" />
          <rect x="21" y="10" width="12" height="12" rx="1" transform="rotate(45 27 16)" fill={bases?.first ? "#f97316" : "#3f3f46"} stroke="#52525b" strokeWidth="0.5" />
          <rect x="3" y="10" width="12" height="12" rx="1" transform="rotate(45 9 16)" fill={bases?.third ? "#f97316" : "#3f3f46"} stroke="#52525b" strokeWidth="0.5" />
        </svg>

        {/* Count */}
        <div className="flex items-center gap-2 sm:gap-3 text-zinc-400 flex-shrink-0">
          <div className="flex items-center gap-1"><span className="text-zinc-600">B</span><span className="text-white font-bold">{balls ?? 0}</span></div>
          <div className="flex items-center gap-1"><span className="text-zinc-600">S</span><span className="text-white font-bold">{strikes ?? 0}</span></div>
          <div className="flex items-center gap-1"><span className="text-zinc-600">O</span><span className="text-white font-bold">{outs ?? 0}</span></div>
        </div>

        {/* At bat / on mound */}
        <div className="flex-1 text-right text-zinc-400 min-w-0 truncate hidden sm:block">
          {at_bat && <span>AB: <span className="text-white">#{at_bat.number} {at_bat.name}</span></span>}
        </div>
      </div>
    </div>
  );
};

const BasketballSituation = ({ situation }) => {
  if (!situation) return null;
  return (
    <div className="overflow-x-auto bg-zinc-800/60 border-x border-b border-white/10">
      <div className="flex items-center justify-between gap-3 px-4 py-2 text-xs min-w-[260px]">
        <div className="flex items-center gap-3 sm:gap-4 text-zinc-400 flex-shrink-0">
          <span className="whitespace-nowrap">Fouls: <span className="text-white">{situation.home_fouls ?? 0}</span> / <span className="text-white">{situation.away_fouls ?? 0}</span></span>
          <span className="whitespace-nowrap">TO: <span className="text-white">{situation.home_timeouts ?? "–"}</span> / <span className="text-white">{situation.away_timeouts ?? "–"}</span></span>
        </div>
        {situation.bonus?.home && <span className="text-orange-400 font-bold flex-shrink-0">BONUS</span>}
      </div>
    </div>
  );
};

const VolleyballSituation = ({ situation }) => {
  if (!situation) return null;
  const results = situation.set_results || [];
  return (
    <div className="overflow-x-auto bg-zinc-800/60 border-x border-b border-white/10">
      <div className="flex items-center justify-between gap-3 px-4 py-2 text-xs min-w-[280px]">
        <span className="text-orange-400 font-bold whitespace-nowrap flex-shrink-0">Set {situation.set || situation.current_set || 1}</span>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {results.map((s, i) => (
            <span key={i} className={`whitespace-nowrap ${s.completed_at ? "text-zinc-400" : "text-white font-bold"}`}>
              S{s.set_num}: {s.home}-{s.away}
            </span>
          ))}
        </div>
        <span className="text-zinc-500 whitespace-nowrap flex-shrink-0">Sets: {situation.sets_won?.home ?? 0} – {situation.sets_won?.away ?? 0}</span>
      </div>
    </div>
  );
};

const SoccerSituation = ({ situation }) => {
  if (!situation) return null;
  return (
    <div className="flex items-center justify-center gap-4 px-4 py-2 bg-zinc-800/60 border-x border-b border-white/10 text-xs text-zinc-400">
      <span className="text-white font-bold whitespace-nowrap">{situation.half || ""}</span>
      {situation.clock && <span className="whitespace-nowrap">{situation.clock}</span>}
    </div>
  );
};

const SituationStrip = ({ sport, situation, status, header }) => {
  if (status !== "active" || !situation) return null;
  const s = sport?.toLowerCase();
  if (s === "football") return <FootballSituation situation={situation} header={header} />;
  if (s === "baseball" || s === "softball") return <BaseballSituation situation={situation} />;
  if (s === "basketball") return <BasketballSituation situation={situation} />;
  if (s === "volleyball") return <VolleyballSituation situation={situation} />;
  if (s === "soccer") return <SoccerSituation situation={situation} />;
  return null;
};


/* ═══════════════════════════════════════════
   BOXSCORE TABLES (sport-specific columns)
   ═══════════════════════════════════════════ */

// Column configs per sport
const BASKETBALL_COLS = [
  { key: "min", label: "MIN" }, { key: "pts", label: "PTS" },
  { key: "fg", label: "FG" }, { key: "three", label: "3PT" }, { key: "ft", label: "FT" },
  { key: "oreb", label: "OR" }, { key: "dreb", label: "DR" }, { key: "treb", label: "REB" },
  { key: "ast", label: "AST" }, { key: "stl", label: "STL" }, { key: "blk", label: "BLK" },
  { key: "to", label: "TO" }, { key: "pf", label: "PF" },
];

const FOOTBALL_PASSING_COLS = [
  { key: "cmp_att", label: "C/A", render: (p) => `${p.cmp ?? 0}/${p.att ?? 0}` },
  { key: "yds", label: "YDS" },
  { key: "td", label: "TD" }, { key: "int", label: "INT" },
  { key: "lng", label: "LNG" },
];

const FOOTBALL_RUSHING_COLS = [
  { key: "att", label: "ATT" }, { key: "yds", label: "YDS" },
  { key: "td", label: "TD" }, { key: "lng", label: "LNG" },
];

const FOOTBALL_RECEIVING_COLS = [
  { key: "rec", label: "REC" }, { key: "yds", label: "YDS" },
  { key: "td", label: "TD" }, { key: "lng", label: "LNG" },
];

const BASEBALL_BATTING_COLS = [
  { key: "ab", label: "AB" }, { key: "r", label: "R" }, { key: "h", label: "H" },
  { key: "rbi", label: "RBI" }, { key: "2b", label: "2B" }, { key: "3b", label: "3B" },
  { key: "hr", label: "HR" }, { key: "bb", label: "BB" }, { key: "k", label: "K" },
];

const BASEBALL_PITCHING_COLS = [
  { key: "ip", label: "IP" }, { key: "h", label: "H" }, { key: "r", label: "R" },
  { key: "er", label: "ER" }, { key: "bb", label: "BB" }, { key: "k", label: "K" },
  { key: "era", label: "ERA" },
];

const SOCCER_FIELD_COLS = [
  { key: "goals", label: "G" }, { key: "assists", label: "A" },
  { key: "shots", label: "SH" }, { key: "sog", label: "SOG" },
  { key: "fouls", label: "F" }, { key: "yellow", label: "YC" }, { key: "red", label: "RC" },
];

const SOCCER_GK_COLS = [
  { key: "saves", label: "SV" }, { key: "ga", label: "GA" },
  { key: "min", label: "MIN" },
];

const VOLLEYBALL_COLS = [
  { key: "k", label: "K" }, { key: "e", label: "E" }, { key: "ta", label: "TA" },
  { key: "pct", label: "PCT" }, { key: "a", label: "A" }, { key: "sa", label: "SA" },
  { key: "se", label: "SE" }, { key: "dig", label: "DIG" },
  { key: "bs", label: "BS" }, { key: "ba", label: "BA" }, { key: "be", label: "BE" },
];

/** Format name as "F. LastName" */
const formatShortName = (name) => {
  if (!name) return "–";
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
};

const StatsTable = ({ players, columns, teamColor }) => {
  if (!players || players.length === 0) return <p className="text-zinc-600 text-xs py-4 text-center">No player data</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-2 px-2 text-zinc-500 font-medium sticky left-0 bg-zinc-900 z-10">PLAYER</th>
            {columns.map(c => (
              <th key={c.key} className="text-right py-2 px-1.5 text-zinc-500 font-medium whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((p, i) => (
            <tr key={p.id || p.player_id || i} className={i % 2 === 0 ? "bg-zinc-800/30" : ""}>
              <td className="py-1.5 px-2 whitespace-nowrap sticky left-0 z-10" style={{ backgroundColor: i % 2 === 0 ? "rgba(39,39,42,0.3)" : "transparent" }}>
                <span className="text-zinc-500 mr-1.5">#{p.number || "–"}</span>
                <span className="text-white font-medium">{formatShortName(p.display_name || p.name)}</span>
              </td>
              {columns.map(c => (
                <td key={c.key} className="text-right py-1.5 px-1.5 text-zinc-300 tabular-nums">
                  {c.render ? c.render(p) : (p[c.key] ?? "–")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── Team Totals Table (from boxscore.{side}.totals) ── */
const TeamTotalsTable = ({ totals }) => {
  if (!totals || Object.keys(totals).length === 0) return <p className="text-zinc-600 text-xs py-4 text-center">No team totals</p>;
  const prettyLabel = (key) => key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const entries = Object.entries(totals).filter(([, v]) => v !== null && v !== undefined);
  return (
    <div className="divide-y divide-white/5">
      {entries.map(([key, val]) => (
        <div key={key} className="flex items-center justify-between px-4 py-1.5 text-xs">
          <span className="text-zinc-500">{prettyLabel(key)}</span>
          <span className="text-white tabular-nums font-medium">{String(val)}</span>
        </div>
      ))}
    </div>
  );
};

const BoxscoreTabs = ({ boxscore, sport, header, teamStatsComparison }) => {
  const [activeTab, setActiveTab] = useState("home");
  const [subTab, setSubTab] = useState("individual"); // "individual" | "teamtotals"
  const s = sport?.toLowerCase();

  const tabs = useMemo(() => {
    const t = [];
    if (header?.home) t.push({ id: "home", label: header.home.abbr || header.home.name });
    if (header?.away) t.push({ id: "away", label: header.away.abbr || header.away.name });
    t.push({ id: "teamstats", label: "Comparison" });
    return t;
  }, [header]);

  const renderIndividualStats = (side) => {
    const data = boxscore?.[side];
    if (!data) return <p className="text-zinc-600 text-xs py-6 text-center">No stats data</p>;

    if (s === "basketball") {
      const players = Array.isArray(data.players) ? data.players : data.players?.rows || [];
      return <StatsTable players={players} columns={BASKETBALL_COLS} />;
    }
    if (s === "football") {
      const cats = data.players || {};
      // API returns { passing: { rows: [], totals: {} }, rushing: { ... }, receiving: { ... } }
      const passing = Array.isArray(cats.passing) ? cats.passing : cats.passing?.rows || [];
      const rushing = Array.isArray(cats.rushing) ? cats.rushing : cats.rushing?.rows || [];
      const receiving = Array.isArray(cats.receiving) ? cats.receiving : cats.receiving?.rows || [];
      const hasCats = passing.length > 0 || rushing.length > 0 || receiving.length > 0;
      if (!hasCats) return <p className="text-zinc-600 text-xs py-6 text-center">No individual player data yet</p>;
      return (
        <div className="space-y-4">
          {passing.length > 0 && (
            <div>
              <h4 className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider px-2 py-1">Passing</h4>
              <StatsTable players={passing} columns={FOOTBALL_PASSING_COLS} />
            </div>
          )}
          {rushing.length > 0 && (
            <div>
              <h4 className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider px-2 py-1">Rushing</h4>
              <StatsTable players={rushing} columns={FOOTBALL_RUSHING_COLS} />
            </div>
          )}
          {receiving.length > 0 && (
            <div>
              <h4 className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider px-2 py-1">Receiving</h4>
              <StatsTable players={receiving} columns={FOOTBALL_RECEIVING_COLS} />
            </div>
          )}
        </div>
      );
    }
    if (s === "baseball" || s === "softball") {
      const batting = Array.isArray(data.batting) ? data.batting : data.batting?.rows || (data.players?.batting?.rows) || [];
      const pitching = Array.isArray(data.pitching) ? data.pitching : data.pitching?.rows || (data.players?.pitching?.rows) || [];
      return (
        <div className="space-y-4">
          {batting.length > 0 && (
            <div>
              <h4 className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider px-2 py-1">Batting</h4>
              <StatsTable players={batting} columns={BASEBALL_BATTING_COLS} />
            </div>
          )}
          {pitching.length > 0 && (
            <div>
              <h4 className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider px-2 py-1">Pitching</h4>
              <StatsTable players={pitching} columns={BASEBALL_PITCHING_COLS} />
            </div>
          )}
        </div>
      );
    }
    if (s === "soccer") {
      const field = Array.isArray(data.field) ? data.field : data.field?.rows || (data.players?.field?.rows) || [];
      const goalkeeper = Array.isArray(data.goalkeeper) ? data.goalkeeper : data.goalkeeper?.rows || (data.players?.goalkeeper?.rows) || [];
      return (
        <div className="space-y-4">
          {field.length > 0 && (
            <div>
              <h4 className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider px-2 py-1">Field Players</h4>
              <StatsTable players={field} columns={SOCCER_FIELD_COLS} />
            </div>
          )}
          {goalkeeper.length > 0 && (
            <div>
              <h4 className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider px-2 py-1">Goalkeeper</h4>
              <StatsTable players={goalkeeper} columns={SOCCER_GK_COLS} />
            </div>
          )}
        </div>
      );
    }
    if (s === "volleyball") {
      const players = Array.isArray(data.players) ? data.players : data.players?.rows || [];
      return <StatsTable players={players} columns={VOLLEYBALL_COLS} />;
    }
    // Fallback
    if (data.players && Array.isArray(data.players) && data.players.length > 0) {
      const cols = Object.keys(data.players[0]).filter(k => !["id","player_id","name","display_name","number","position","team_id"].includes(k)).map(k => ({ key: k, label: k.toUpperCase() }));
      return <StatsTable players={data.players} columns={cols.slice(0, 12)} />;
    }
    return <p className="text-zinc-600 text-xs py-6 text-center">No data available</p>;
  };

  const renderTeamTab = (side) => {
    const data = boxscore?.[side];
    return (
      <div>
        {/* Sub-tab bar */}
        <div className="flex border-b border-white/5">
          <button
            onClick={() => setSubTab("individual")}
            className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${
              subTab === "individual" ? "text-white bg-zinc-800/50 border-b border-white/20" : "text-zinc-600 hover:text-zinc-400"
            }`}
            data-testid={`gamecast-subtab-individual`}
          >
            Individual Stats
          </button>
          <button
            onClick={() => setSubTab("teamtotals")}
            className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${
              subTab === "teamtotals" ? "text-white bg-zinc-800/50 border-b border-white/20" : "text-zinc-600 hover:text-zinc-400"
            }`}
            data-testid={`gamecast-subtab-teamtotals`}
          >
            Team Totals
          </button>
        </div>

        {/* Sub-tab content */}
        {subTab === "individual"
          ? renderIndividualStats(side)
          : <TeamTotalsTable totals={data?.totals} />
        }
      </div>
    );
  };

  return (
    <div className="bg-zinc-900 border border-white/10" data-testid="gamecast-boxscore">
      {/* Tab bar — scrollable on mobile */}
      <div className="flex border-b border-white/10 overflow-x-auto scrollbar-hide">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => { setActiveTab(t.id); setSubTab("individual"); }}
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap min-w-[80px] ${
              activeTab === t.id
                ? "text-orange-500 border-b-2 border-orange-500 bg-zinc-800/30"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid={`gamecast-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content — scrollable tables */}
      <div className="p-0 overflow-x-auto">
        {activeTab === "away" && renderTeamTab("away")}
        {activeTab === "home" && renderTeamTab("home")}
        {activeTab === "teamstats" && <TeamStatsComparisonContent data={teamStatsComparison} header={header} />}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   PLAY-BY-PLAY FEED
   ═══════════════════════════════════════════ */
const PlayByPlayFeed = ({ plays, scoringSummary, header }) => {
  const [scoringOnly, setScoringOnly] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const displayPlays = useMemo(() => {
    const source = scoringOnly ? (scoringSummary || []) : (plays || []);
    // Reverse so newest is on top
    return [...source].reverse();
  }, [plays, scoringSummary, scoringOnly]);

  if (!plays || plays.length === 0) {
    return (
      <div className="bg-zinc-900 border border-white/10 p-6 text-center" data-testid="gamecast-pbp">
        <Activity className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
        <p className="text-zinc-600 text-xs">No plays recorded yet</p>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-white/10" data-testid="gamecast-pbp">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-white font-bold text-xs uppercase tracking-wider">
          <Activity className="w-3.5 h-3.5 text-orange-500" />
          Play-by-Play
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => setScoringOnly(!scoringOnly)}
          className={`flex items-center gap-1.5 text-[10px] font-bold uppercase px-2.5 py-1 rounded transition-colors ${
            scoringOnly ? "bg-orange-500 text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"
          }`}
          data-testid="gamecast-scoring-toggle"
        >
          <Filter className="w-3 h-3" />
          Scoring Only
        </button>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="max-h-[400px] overflow-y-auto"
          >
            {displayPlays.map((play, i) => {
              const isScoring = play.scoring_play;
              const teamColor = play.team === "home" ? header?.home?.color : header?.away?.color;
              const tier = isScoring ? getScoreTier(play.action) : null;
              const badgeColor = tier ? tier.color : null;
              return (
                <div
                  key={play.id || i}
                  className={`px-3 sm:px-4 py-2.5 border-b border-white/5 ${i % 2 === 0 ? "bg-zinc-800/20" : ""} ${isScoring ? "border-l-2" : ""}`}
                  style={isScoring ? { borderLeftColor: badgeColor || teamColor || "#f97316" } : {}}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-10 sm:w-14 text-right">
                      <span className="text-zinc-600 text-[9px] sm:text-[10px]">{play.period_label} {play.period}</span>
                      <br />
                      <span className="text-zinc-500 text-[9px] sm:text-[10px] tabular-nums">{play.clock}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                        {play.player && (
                          <span className="text-white text-[11px] sm:text-xs font-medium whitespace-nowrap">
                            {play.player.number && `#${play.player.number} `}{formatShortName(play.player.name)}
                          </span>
                        )}
                        <span
                          className={`text-[9px] sm:text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${isScoring ? "text-black" : "text-zinc-400 bg-zinc-800"}`}
                          style={isScoring ? { backgroundColor: badgeColor || teamColor || "#f97316" } : {}}
                        >
                          {play.action}
                        </span>
                      </div>
                      {play.description && <p className="text-zinc-500 text-[11px] sm:text-xs mt-0.5 leading-relaxed break-words">{play.description}</p>}
                    </div>
                    {(play.home_score != null || play.away_score != null) && (
                      <div className="flex-shrink-0 text-right">
                        <span className="text-zinc-400 text-[9px] sm:text-[10px] tabular-nums whitespace-nowrap">{play.away_score ?? "–"} – {play.home_score ?? "–"}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


/* ═══════════════════════════════════════════
   TEAM STATS COMPARISON BAR
   ═══════════════════════════════════════════ */
const TeamStatsComparisonContent = ({ data, header }) => {
  // data can be {home: {...}, away: {...}} or {cumulatives: {home: {...}, away: {...}}}
  let home, away;
  if (data?.cumulatives) {
    // Football format with nested cumulatives
    const hc = data.cumulatives.home || {};
    const ac = data.cumulatives.away || {};
    // Flatten all nested categories
    home = {};
    away = {};
    for (const cat of Object.values(hc)) {
      if (typeof cat === "object") Object.assign(home, cat);
    }
    for (const cat of Object.values(ac)) {
      if (typeof cat === "object") Object.assign(away, cat);
    }
  } else {
    home = data?.home || {};
    away = data?.away || {};
  }

  // Merge all keys
  const allKeys = [...new Set([...Object.keys(home), ...Object.keys(away)])];
  if (allKeys.length === 0) return <p className="text-zinc-600 text-xs py-6 text-center">No team stats</p>;

  // Pretty label
  const prettyLabel = (key) => key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  // Filter to show most useful stats (skip overly detailed ones)
  const displayKeys = allKeys.filter(k => {
    const v = home[k] ?? away[k];
    return v !== null && v !== undefined;
  }).slice(0, 16);

  return (
    <div className="px-4 py-3 space-y-2.5">
      {displayKeys.map(key => {
        const hv = home[key];
        const av = away[key];
        const hvNum = typeof hv === "number" ? hv : parseFloat(String(hv).replace(/[^0-9.]/g, "")) || 0;
        const avNum = typeof av === "number" ? av : parseFloat(String(av).replace(/[^0-9.]/g, "")) || 0;
        const max = Math.max(hvNum, avNum, 1);
        const hPct = (hvNum / max) * 100;
        const aPct = (avNum / max) * 100;

        return (
          <div key={key}>
            <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-0.5 uppercase tracking-wider">
              <span className="text-white tabular-nums">{String(av ?? "–")}</span>
              <span>{prettyLabel(key)}</span>
              <span className="text-white tabular-nums">{String(hv ?? "–")}</span>
            </div>
            <div className="flex gap-1 h-1.5">
              <div className="flex-1 bg-zinc-800 rounded-full overflow-hidden flex justify-end">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${aPct}%`, backgroundColor: header?.away?.color || "#0ea5e9" }} />
              </div>
              <div className="flex-1 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${hPct}%`, backgroundColor: header?.home?.color || "#f97316" }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TeamStatsComparison = ({ data, header }) => {
  if (!data) return null;
  return (
    <div className="bg-zinc-900 border border-white/10" data-testid="gamecast-teamstats">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
        <span className="text-white font-bold text-xs uppercase tracking-wider flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-orange-500" />
          Team Stats
        </span>
        <div className="flex items-center gap-4 text-[10px] font-bold uppercase">
          <span style={{ color: header?.away?.color }}>{header?.away?.abbr}</span>
          <span style={{ color: header?.home?.color }}>{header?.home?.abbr}</span>
        </div>
      </div>
      <TeamStatsComparisonContent data={data} header={header} />
    </div>
  );
};


/* ═══════════════════════════════════════════
   LEADERS PANEL (football + basketball)
   ═══════════════════════════════════════════ */
const LeadersPanel = ({ leaders, sport, header }) => {
  if (!leaders) return null;
  const s = sport?.toLowerCase();
  if (s !== "football" && s !== "basketball") return null;

  const hasData = Object.values(leaders.home || {}).some(v => v) || Object.values(leaders.away || {}).some(v => v);
  if (!hasData) return null;

  const categories = s === "football" ? ["passing", "rushing", "receiving"] : ["points", "rebounds", "assists"];
  const catLabels = s === "football"
    ? { passing: "Passing", rushing: "Rushing", receiving: "Receiving" }
    : { points: "Points", rebounds: "Rebounds", assists: "Assists" };

  const awayTeam = header?.away || {};
  const homeTeam = header?.home || {};

  const renderLeaderName = (leader) => {
    if (!leader) return <span className="text-zinc-700 text-xs">–</span>;
    const name = leader.name || leader.player || "";
    return (
      <span className="text-white text-xs font-medium truncate">
        {leader.number ? `#${leader.number} ` : ""}{formatShortName(name)}
      </span>
    );
  };

  return (
    <div className="bg-zinc-900 border border-white/10" data-testid="gamecast-leaders">
      <div className="px-3 sm:px-4 py-2.5 border-b border-white/10">
        <span className="text-white font-bold text-xs uppercase tracking-wider flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5 text-orange-500" />
          Leaders
        </span>
      </div>

      {/* Team headers with logos */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-white/10 bg-zinc-800/40">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <TeamLogo team={awayTeam} size={20} />
          <span className="text-zinc-300 text-[10px] font-bold uppercase truncate">{awayTeam.name || awayTeam.abbr || "Away"}</span>
        </div>
        <div className="w-px h-5 bg-zinc-600 mx-3 flex-shrink-0" />
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <span className="text-zinc-300 text-[10px] font-bold uppercase truncate text-right">{homeTeam.name || homeTeam.abbr || "Home"}</span>
          <TeamLogo team={homeTeam} size={20} />
        </div>
      </div>

      {/* Stat categories */}
      <div className="divide-y divide-white/5">
        {categories.map(cat => {
          const homeL = leaders.home?.[cat];
          const awayL = leaders.away?.[cat];
          if (!homeL && !awayL) return null;
          return (
            <div key={cat} className="px-3 sm:px-4 py-2.5">
              <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider mb-2 text-center">{catLabels[cat]}</p>
              <div className="flex items-center gap-0">
                {/* Away leader */}
                <div className="flex-1 min-w-0 text-left">
                  <div className="truncate">{renderLeaderName(awayL)}</div>
                  {awayL && <p className="text-zinc-500 text-[10px] mt-0.5">{awayL.value ?? awayL.yards ?? awayL.stat_value}</p>}
                </div>
                {/* Bar separator */}
                <div className="w-px h-8 bg-zinc-600 mx-3 flex-shrink-0" />
                {/* Home leader */}
                <div className="flex-1 min-w-0 text-right">
                  <div className="truncate text-right">{renderLeaderName(homeL)}</div>
                  {homeL && <p className="text-zinc-500 text-[10px] mt-0.5">{homeL.value ?? homeL.yards ?? homeL.stat_value}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export const StatMooseGameCast = ({ code, className = "", hideHeader = false, broadcastOverrides = null }) => {
  const { data, error, transport } = useGameCastData(code);

  if (!data && !error) {
    return (
      <div className={`${className}`} data-testid="gamecast-loading">
        <div className="bg-zinc-900 border border-white/10 p-8 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-zinc-700 border-t-orange-500 rounded-full animate-spin" />
          <span className="text-zinc-500 text-xs ml-3">Loading GameCast...</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={`${className}`} data-testid="gamecast-error">
        <div className="bg-zinc-900 border border-red-500/30 p-6 text-center">
          <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
          <p className="text-zinc-400 text-xs">{error}</p>
        </div>
      </div>
    );
  }

  const { header, boxscore, plays, scoring_summary, team_stats_comparison, leaders, situation } = data;

  return (
    <div className={`space-y-0 ${className}`} data-testid="gamecast-container">
      {/* Header bar — hidden when rendered externally at page level */}
      {!hideHeader && <GameCastHeader header={header} broadcastOverrides={broadcastOverrides} />}

      {/* Situation strip */}
      <SituationStrip sport={header?.sport} situation={situation} status={header?.status} header={header} />

      {/* Transport indicator */}
      <div className="flex justify-end px-1 py-0.5">
        <span className={`text-[9px] flex items-center gap-1 ${transport === "sse" ? "text-green-600" : transport === "polling" ? "text-yellow-600" : "text-zinc-600"}`}>
          <Circle className="w-1.5 h-1.5 fill-current" />
          {transport === "sse" ? "Live" : transport === "polling" ? "Polling" : "Connecting"}
        </span>
      </div>

      {/* Boxscore */}
      <BoxscoreTabs boxscore={boxscore} sport={header?.sport} header={header} teamStatsComparison={team_stats_comparison} />

      {/* Team Stats Comparison — now inside boxscore tabs, but keep standalone for non-tab view */}

      {/* Leaders (FB + BKB) */}
      <LeadersPanel leaders={leaders} sport={header?.sport} header={header} />

      {/* Play-by-Play */}
      <PlayByPlayFeed plays={plays} scoringSummary={scoring_summary} header={header} />
    </div>
  );
};

// Export hook and header for page-level usage
export { useGameCastData, GameCastHeader };
export default StatMooseGameCast;
